// import node module libraries
// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Form, Image, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';
import { FormSelect } from 'components/elements/form-select/FormSelect';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import PersonnelService from '../../services/PersonnelService';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const OldPersonnelsListItems = () => {
	const [originalPersonnels, setOriginalPersonnels] = useState([]);
	const [personnels, setPersonnels] = useState([]);

	useEffect(() => {
		Promise.all([PersonnelService.getAllNonActivePersonnels()]).then(
			([personnelsData]) => {
				setOriginalPersonnels(personnelsData);
				setPersonnels(personnelsData);
			}
		);
	}, []);

	const POSTE_CHOICES = [
		{ value: 'enseignant', label: 'Enseignant(e)' },
		{ value: 'enseignant_principal', label: 'Enseignant Principal' },
		{ value: 'surveillant_general', label: 'Surveillant Général' },
		{ value: 'conseiller_pedagogique', label: 'Conseiller Pédagogique' },
		{ value: 'infirmier', label: 'Infirmier' },
		{ value: 'bibliothecaire', label: 'Bibliothécaire' },
		{ value: 'technicien_informatique', label: 'Technicien Informatique' },
		{ value: 'agent_de_service', label: 'Agent de Service' },
		{ value: 'gardien', label: 'Gardien' },
		{ value: 'chauffeur', label: 'Chauffeur' },
		{ value: 'cantine', label: 'Responsable de la Cantine' },
		{ value: 'psychologue_scolaire', label: 'Psychologue Scolaire' },
		{ value: 'educateur_specialise', label: 'Éducateur Spécialisé' },
		{ value: 'assistant_pedagogique', label: 'Assistant Pédagogique' },
		{ value: 'assistant_administratif', label: 'Assistant Administratif' },
		{ value: 'autre', label: 'Autre' }
	];

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;

		// Créez un tableau des valeurs possibles pour le poste
		const posteChoicesValues = POSTE_CHOICES.map((choice) => choice.value);

		// Condition pour filtrer les autres postes non listés dans POSTE_CHOICES
		if (filterTerm === 'autre') {
			const newPersonnelsList = originalPersonnels.filter((personnel) => {
				// Ne renvoie que les postes qui ne sont pas dans le tableau posteChoicesValues
				return !posteChoicesValues.includes(personnel.poste);
			});
			setPersonnels(newPersonnelsList);
		}
		// Condition pour filtrer en fonction du poste choisi
		else if (filterTerm !== '') {
			const newPersonnelsList = originalPersonnels.filter((personnel) => {
				return personnel.poste === filterTerm;
			});
			setPersonnels(newPersonnelsList);
		}
		// Si aucun filtre n'est sélectionné, réinitialisez la liste
		else {
			setPersonnels(originalPersonnels);
		}
	};

	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (columnId === 'nom' && value) {
				return value.toUpperCase();
			} else if (
				columnId === 'prenom' ||
				columnId === 'genre' ||
				(columnId === 'adresse' && value)
			) {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else {
				return value;
			}
		}
		return value;
	};

	const formatPoste = (value) => {
		return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const getInitials = (personnel) => {
		const firstName = personnel.prenom
			? personnel.prenom.charAt(0).toUpperCase()
			: '';
		const lastName = personnel.nom ? personnel.nom.charAt(0).toUpperCase() : '';
		return `${lastName} ${firstName}`;
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'nom',
				Header: 'Nom',
				Cell: ({ value, row }) => {
					const initials = getInitials(row.original);
					const imageUrl = `${backendUrl}${row.original.photo}`;
					return (
						<div className="d-flex align-items-center">
							{row.original.photo ? (
								<Image
									src={imageUrl}
									alt=""
									className="rounded-circle avatar-md"
								/>
							) : (
								<Avatar
									type="initial"
									size="md"
									name={initials}
									className="rounded-circle"
									alt={initials}
								/>
							)}
							{value ? (
								<Link to={`/archives/personnel/${row.original.id}/`}>
									<h5 className="mb-0 ms-2">{formatValue(value, 'nom')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},

			{
				accessor: 'prenom',
				Header: 'Prenom',
				Cell: ({ value }) => <div>{formatValue(value, 'prenom')}</div>
			},
			{
				accessor: 'genre',
				Header: 'Genre',
				Cell: ({ value }) => <div>{formatValue(value, 'genre')}</div>
			},
			{
				accessor: 'adresse',
				Header: 'Adresse',
				Cell: ({ value }) => <div>{formatValue(value, 'adresse')}</div>
			},
			{
				accessor: 'telephone',
				Header: 'Téléphone',
				Cell: ({ value }) => <div>{formatValue(value, 'telephone')}</div>
			},
			{
				accessor: 'email',
				Header: 'Email',
				Cell: ({ value }) => <div>{value}</div>
			},
			{
				accessor: 'poste',
				Header: 'Poste',
				Cell: ({ value }) => <div>{formatPoste(value)}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return personnels.map((personnel) => ({
			...personnel
		}));
	}, [personnels]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xxl={2} lg={2} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher un personnel"
						/>
					</Col>
					<Col xxl={2} lg={2} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par poste"
							options={POSTE_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default OldPersonnelsListItems;
